<template>
  <div class="space-y-4">
    <div class="space-y-3">
      <div class="flex flex-wrap gap-2 border border-secondary rounded-md p-2 bg-secondary">
        <CommunicationTemplatePlaceholderPicker
          :placeholders="placeholders"
          @select="insertPlaceholder"
        />
        <Button
          label="Bold"
          icon="bold"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          :class="{ 'editor-toolbar-button-active': editor?.isActive('bold') }"
          @click="toggleBold"
        />
        <Button
          label="Italic"
          icon="italic"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          :class="{ 'editor-toolbar-button-active': editor?.isActive('italic') }"
          @click="toggleItalic"
        />
        <Button
          label="Bullet list"
          icon="list"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          :class="{ 'editor-toolbar-button-active': editor?.isActive('bulletList') }"
          @click="toggleBulletList"
        />
        <Button
          label="Numbered list"
          icon="list-ol"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          :class="{ 'editor-toolbar-button-active': editor?.isActive('orderedList') }"
          @click="toggleOrderedList"
        />
        <Button
          label="Link"
          icon="link"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          :class="{ 'editor-toolbar-button-active': editor?.isActive('link') }"
          @click="setLink"
        />
        <Button
          label="Remove link"
          icon="link-slash"
          severity="secondary"
          outlined
          size="sm"
          :disabled="disabled || !editor"
          @click="clearLink"
        />
      </div>

      <div
        class="border border-secondary rounded-md overflow-hidden"
        :class="{ 'template-editor-error': hasError }"
      >
        <EditorContent
          v-if="editor"
          :editor="editor"
          class="template-editor-content"
        />
      </div>
    </div>

    <InputError :has-error="hasError">
      {{ errorMessage }}
    </InputError>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'

// libraries
import { Extension } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Plugin } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'

// components
import Button from '@/components/button/Button.vue'
import InputError from '@/components/forms/InputError.vue'
import CommunicationTemplatePlaceholderPicker from '@/components/unique/communications/CommunicationTemplatePlaceholderPicker.vue'

// types
import type { CommunicationTemplatePlaceholderOption } from '@/utilities/communications/templatePlaceholders'

/**
 * Props accepted by the email editor wrapper.
 */
interface Props {
  htmlValue: string
  placeholders: CommunicationTemplatePlaceholderOption[]
  disabled?: boolean
  hasError?: boolean
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  hasError: false,
  errorMessage: ''
})

const emit = defineEmits<{
  'update:htmlValue': [value: string]
}>()

/**
 * Adds editor-only badge styling to placeholder tokens without changing saved HTML.
 */
const PlaceholderBadgeExtension = Extension.create({
  name: 'placeholderBadge',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations(state) {
            const decorations: Decoration[] = []

            state.doc.descendants((node, position) => {
              if (!node.isText || !node.text) {
                return
              }

              const placeholderPattern = /\{\{[^{}\n]+\}\}/g
              let match: RegExpExecArray | null

              while ((match = placeholderPattern.exec(node.text)) !== null) {
                decorations.push(
                  Decoration.inline(position + match.index, position + match.index + match[0].length, {
                    class: 'template-placeholder-badge'
                  })
                )
              }
            })

            return DecorationSet.create(state.doc, decorations)
          }
        }
      })
    ]
  }
})

/**
 * Tiptap editor instance used for rich HTML authoring.
 */
const editor = useEditor({
  content: normalizeHtmlForEditor(props.htmlValue),
  editable: !props.disabled,
  extensions: [
    StarterKit,
    Link.configure({
      openOnClick: false
    }),
    Placeholder.configure({
      placeholder: 'Write your HTML email here...'
    }),
    PlaceholderBadgeExtension
  ],
  onUpdate: ({ editor: currentEditor }) => {
    emit('update:htmlValue', currentEditor.getHTML())
  }
})

watch(
  () => props.htmlValue,
  newValue => {
    if (!editor.value) {
      return
    }

    const nextHtml = normalizeHtmlForEditor(newValue)
    const currentHtml = editor.value.getHTML()

    // Avoid resetting the cursor when the editor already holds the same content.
    if (currentHtml === nextHtml) {
      return
    }

    editor.value.commands.setContent(nextHtml, { emitUpdate: false })
  }
)

watch(
  () => props.disabled,
  newValue => {
    editor.value?.setEditable(!newValue)
  }
)

/**
 * Inserts a placeholder into the current editor selection.
 */
function insertPlaceholder(token: string): void {
  editor.value?.chain().focus().insertContent(token).run()
}

/**
 * Converts legacy br/newline bodies into paragraph blocks so list actions target individual lines.
 */
function normalizeHtmlForEditor(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '<p></p>'
  }

  const hasStructuredBlockMarkup = /<\/?(ul|ol|li|h[1-6]|table|blockquote|section|article)[\s>]/i.test(value)
  const hasLineBreaks = /<br\s*\/?>|\r?\n/i.test(value)

  if (hasStructuredBlockMarkup || !hasLineBreaks) {
    return value
  }

  return value
    .replace(/\r\n/g, '\n')
    .replace(/<(p|div)(?:\s[^>]*)?>/gi, '')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map(line => (line.trim().length ? `<p>${line}</p>` : '<p></p>'))
    .join('')
}

/**
 * Applies bold formatting to the current selection.
 */
function toggleBold(): void {
  editor.value?.chain().focus().toggleBold().run()
}

/**
 * Applies italic formatting to the current selection.
 */
function toggleItalic(): void {
  editor.value?.chain().focus().toggleItalic().run()
}

/**
 * Toggles a bulleted list for the current block.
 */
function toggleBulletList(): void {
  editor.value?.chain().focus().toggleBulletList().run()
}

/**
 * Toggles a numbered list for the current block.
 */
function toggleOrderedList(): void {
  editor.value?.chain().focus().toggleOrderedList().run()
}

/**
 * Adds or updates the active link.
 */
function setLink(): void {
  if (!editor.value) {
    return
  }

  const previousUrl = editor.value.getAttributes('link').href as string | undefined
  const url = window.prompt('Enter a URL', previousUrl || 'https://')

  // Empty input clears the link instead of leaving broken markup behind.
  if (url === null) {
    return
  }

  if (url.trim().length === 0) {
    clearLink()
    return
  }

  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
}

/**
 * Removes the active link mark from the current selection.
 */
function clearLink(): void {
  editor.value?.chain().focus().unsetLink().run()
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.editor-toolbar-button-active {
  border-color: var(--border-brand);
  background-color: var(--bg-brand-light);
}

.template-editor-content :deep(.ProseMirror) {
  min-height: 18rem;
  padding: var(--s-4);
  outline: none;
}

.template-editor-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: var(--text-tertiary);
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.template-editor-content :deep(.ProseMirror ul),
.template-editor-content :deep(.ProseMirror ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.template-editor-content :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.template-editor-content :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.template-editor-content :deep(.ProseMirror li) {
  display: list-item;
  padding-left: 0.25rem;
}

.template-editor-content :deep(.ProseMirror li p) {
  margin: 0;
}

.template-editor-content :deep(.ProseMirror a) {
  color: var(--text-brand);
  text-decoration: underline;
}

.template-editor-content :deep(.template-placeholder-badge) {
  display: inline-block;
  padding: 0.0625rem 0.375rem;
  border: 1px solid var(--border-brand);
  border-radius: var(--rounded-full);
  background-color: var(--bg-brand-light);
  color: var(--text-brand);
  font-size: 0.875em;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.template-editor-error {
  border-color: var(--border-error);
}
</style>
